import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProjectImageProps {
  src: string;
  alt: string;
  isMobile?: boolean;
  className?: string;
  screens?: string[]; // Array of screen images for mobile
  size?: 'normal' | 'large' | 'featured'; // Size variant for mobile mockups
}

const ImageContainer = styled(motion.div)<{ $isMobile: boolean; $size?: string }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: 100%;
  border-radius: 22px;
  overflow: visible;
  margin: 0;
  padding: 0;
  background: transparent;
  
  // Ombre pour le téléphone
  ${props => props.$isMobile ? `
    filter: drop-shadow(0 8px 32px rgba(0, 0, 0, 0.3)) drop-shadow(0 4px 16px rgba(0, 0, 0, 0.2));
  ` : ''}
`;

const PhoneMockup = styled.div<{ $size?: string }>`
  display: inline-block;
  background: #1a1a1a;
  border-radius: 22px;
  padding: 8px;
  border: 2px solid #333;
`;

const PhoneScreen = styled.img<{ $size?: string }>`
  display: block;
  max-height: ${props => {
    if (props.$size === 'featured') return '600px';
    if (props.$size === 'large') return '450px';
    return '300px';
  }};
  width: auto;
  border-radius: 14px;
  border: none;
  outline: none;
  background: #000;
`;

const NavigationContainer = styled.div<{ $size?: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
`;

const ChevronButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  &:first-child {
    left: -60px;  /* Chevron gauche */
  }
  
  &:last-child {
    right: -60px; /* Chevron droite */
  }
`;

const DotsContainer = styled.div<{ $size?: string }>`
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  pointer-events: auto;
`;

const Dot = styled(motion.button)<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.4)'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  }
`;

const DesktopImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: top;
  border-radius: ${props => props.theme.borderRadius.xl};
  margin: 0;
  padding: 0;
  display: block;
`;

const ProjectImage: React.FC<ProjectImageProps> = ({
  src,
  alt,
  isMobile = false,
  className,
  screens = [],
  size = 'normal'
}) => {
  const [currentScreen, setCurrentScreen] = useState(0);
  
  // Use screens array if provided, otherwise fallback to single src
  const allScreens = screens.length > 0 ? screens : [src];
  
  const nextScreen = () => {
    setCurrentScreen((prev) => (prev + 1) % allScreens.length);
  };
  
  const prevScreen = () => {
    setCurrentScreen((prev) => (prev - 1 + allScreens.length) % allScreens.length);
  };
  
  const goToScreen = (index: number) => {
    setCurrentScreen(index);
  };

  return (
    <ImageContainer
      className={className}
      $isMobile={isMobile}
      $size={size}
      whileHover={{ 
        scale: 1.02,
        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.2), 0 6px 12px rgba(0, 0, 0, 0.15)"
      }}
      transition={{ duration: 0.3 }}
    >
      {isMobile ? (
        <>
          <PhoneMockup $size={size}>
            <PhoneScreen 
              src={allScreens[currentScreen]} 
              alt={`${alt} - Screen ${currentScreen + 1}`}
              $size={size}
            />
            
            {allScreens.length > 1 && (
              <DotsContainer $size={size}>
                {allScreens.map((_, index) => (
                  <Dot
                    key={index}
                    $active={index === currentScreen}
                    onClick={() => goToScreen(index)}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.8 }}
                  />
                ))}
              </DotsContainer>
            )}
          </PhoneMockup>
          
          {allScreens.length > 1 && (
            <NavigationContainer $size={size}>
              <ChevronButton
                onClick={prevScreen}
                disabled={allScreens.length <= 1}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronLeft size={18} />
              </ChevronButton>
              
              <ChevronButton
                onClick={nextScreen}
                disabled={allScreens.length <= 1}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronRight size={18} />
              </ChevronButton>
            </NavigationContainer>
          )}
        </>
      ) : (
        <DesktopImage src={src} alt={alt} />
      )}
    </ImageContainer>
  );
};

export default ProjectImage;
